import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import { defaultHistoryProgress } from '@/types/history/history-progress';
import type {
    IHistoryDemographicsFormValues,
    IHistoryDemographicsModel,
    IHistoryDemographicsSubmitResult,
} from '@/types/history/history-demographics';

const HISTORY_DEMOGRAPHICS_ENDPOINT = '/api/register/history-demographics';

const defaultHistoryDemographicsModel: IHistoryDemographicsModel = {
    ...defaultHistoryProgress,
    currentRegistrationStep: DashRegistrationStep.HistoryDemographics,

    askForId: false,
    isLwrAvailable: false,
    isDcnSite: false,
    isNkrSite: false,

    identificationTab: 'ssn',

    wageReimbursementPtoValues: [],
    usCountryValue: 0,

    ethnicity: null,

    ssn: '',
    dlIssueCountry: null,
    dlIssueState: null,
    dlNonUsStateProvince: '',
    dlNumber: '',
    mothersMaiden: '',

    emergencyContact: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: null,

    isCurrentlyEmployed: null,
    occupation: '',
    occupationHasHeavyLifting: null,
    medicalPto: null,
    wageReimbursementImportance: null,
    annualIncomeRange: null,
    wantsWageReimbursement: null,

    hasHealthInsurance: null,
    hasPrimaryCarePhysician: null,
    physicianName: '',

    ethnicityOptions: [],
    stateOptions: [],
    countryOptions: [],
    relationshipToContactOptions: [],
    medicalPtoOptions: [],
    wageImportanceOptions: [],
    annualIncomeOptions: [],
};

export const useHistoryDemographics = (registrationId: number, editKey: string) => {
    const url = `${HISTORY_DEMOGRAPHICS_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel(url, defaultHistoryDemographicsModel);

    const { submit, isSubmitting, submitError } = useApiSubmit<
        IHistoryDemographicsFormValues,
        DashRegistrationStep,
        IHistoryDemographicsSubmitResult
    >(
        url,
        (values) => ({
            ethnicity: toIntOrNull(values.ethnicity),

            ssn: values.ssn,
            dlIssueCountry: toIntOrNull(values.dlIssueCountry),
            dlIssueState: toIntOrNull(values.dlIssueState),
            dlNonUsStateProvince: values.dlNonUsStateProvince,
            dlNumber: values.dlNumber,
            mothersMaiden: values.mothersMaiden,

            emergencyContact: values.emergencyContact,
            emergencyContactPhone: values.emergencyContactPhone,
            emergencyContactRelationship: toIntOrNull(values.emergencyContactRelationship),

            isCurrentlyEmployed: toBoolOrNull(values.isCurrentlyEmployed),
            occupation: values.occupation,
            occupationHasHeavyLifting: toBoolOrNull(values.occupationHasHeavyLifting),
            medicalPto: toIntOrNull(values.medicalPto),
            wageReimbursementImportance: toIntOrNull(values.wageReimbursementImportance),
            annualIncomeRange: toIntOrNull(values.annualIncomeRange),
            wantsWageReimbursement: toBoolOrNull(values.wantsWageReimbursement),

            hasHealthInsurance: toBoolOrNull(values.hasHealthInsurance),
            hasPrimaryCarePhysician: toBoolOrNull(values.hasPrimaryCarePhysician),
            physicianName: values.physicianName,
        }),
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
